import React, { MouseEventHandler } from 'react';
import PropTypes, { InferProps } from 'prop-types';
import styled from '@emotion/styled';
import MUITextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import { Theme } from '@mui/material/styles';
import SearchField from '../SearchField';
import { StyledChip } from '../Chip';
import Skeleton from '../SkeletonLoader/Skeleton';
import Checkbox from '../Checkbox';
import coalesceWithEmptyString from '../lib/coalesceWithEmptyString';
import breakpoints from '../Theme/breakpoints';

const SearchInput = styled.div`
  grid-area: searchInput;
  margin: 0 0 8px 0;
  min-width: 0;
`;

const LocationInput = styled.div`
  grid-area: locationInput;
  margin: 0 0 4px 0;
  min-width: 0;
`;

const LocationBadges = styled.div`
  grid-area: locationBadges;
  padding: 0;
  min-width: 0;
  min-height: 48px;
  margin: .5rem -.75rem;

  @media (min-width: ${breakpoints.sm}px) {
    margin: 0;
  }
`;

const CategoryBadges = styled.div`
  grid-area: categoryBadges;
  padding: 0;
  min-width: 0;
  min-height: 48px;
  margin: .5rem -.75rem;

  @media (min-width: ${breakpoints.sm}px) {
    margin: 0;
  }
`;

const AdditionalOptions = styled.div`
  grid-area: additionalOptions;
  padding: 0;
  min-width: 0;
`;

const FullWidthSearchField = styled(SearchField)`
  width: 100%;
`;

const LocationSearchContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 40px;
  gap: 8px;
  align-items: center;
`;

const JobsNearMeIconButton = styled(IconButton)<{
  theme?: Theme;
  selected?: boolean;
}>`
  min-width: 40px;
  height: 40px;
  background-color: ${({ theme, selected }) => (selected
    ? theme.colors.primary
    : theme.colors.actionHover)
};
  color: ${({ theme, selected }) => (selected
    ? theme.colors.primaryContrastText
    : theme.colors.blackHigh)
};

  &:hover {
    background-color: ${({ theme, selected }) => (selected
    ? theme.colors.primaryDark
    : theme.colors.actionSelected)
};
  }
`;

const ContainerScrollable = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  align-items: center;
  height: 48px;
  justify-content: flex-start;
  overflow-x: auto;
  overflow-y: hidden;

  @media (min-width: 1024px) {
    flex-wrap: wrap;
    justify-content: center;
    overflow-x: visible;
  }
`;

const JobsStyledChip = styled(StyledChip, {
  shouldForwardProp: (property) => property !== 'hasCount',
})<{ hasCount?: boolean }>`
  font-size: 12px;
  background-color: #f5f5f5;
  border: 1px solid #e0e0e0;
  vertical-align: baseline;

  &.MuiChip-colorPrimary {
    border-color: transparent;
  }

  .MuiChip-label {
    padding-right: ${({ hasCount }) => (hasCount ? '8px' : '12px')};
    overflow: visible;
  }
`;

const ChipCount = styled.span<{ selected?: boolean; theme?: Theme }>`
  background: white;
  color: ${({ selected, theme }) => (
    selected ? theme.colors.primary : theme.colors.blackHigh
  )};
  padding: 2px 6px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  text-align: center;
  line-height: 1.2;
  margin-left: 8px;
`;

const searchPropertyTypes = {
  keywords: PropTypes.string,
  searchFieldTitle: PropTypes.string.isRequired,
  onSearchTrailingIconSelect: PropTypes.func.isRequired,
  onSearchChange: PropTypes.func.isRequired,
  locationSearch: PropTypes.string,
  locationSearchFieldTitle: PropTypes.string.isRequired,
  onLocationSearchTrailingIconSelect: PropTypes.func.isRequired,
  onLocationSearchChange: PropTypes.func.isRequired,
  categoryOptions: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      onClick: PropTypes.func.isRequired,
      selected: PropTypes.bool.isRequired,
      jobsCount: PropTypes.number,
    }).isRequired,
  ).isRequired,
  cities: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      selected: PropTypes.bool.isRequired,
      onClick: PropTypes.func.isRequired,
      jobsCount: PropTypes.number,
    }),
  ).isRequired,
  sortByDistanceInsertion: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      selected: PropTypes.bool.isRequired,
      onClick: PropTypes.func.isRequired,
    }).isRequired,
  ).isRequired,
  showJobsPageLoader: PropTypes.bool,
  showCitiesLoader: PropTypes.bool,
  handleAllLocalesFilterChange: PropTypes.func,
  showJobsForAllLocales: PropTypes.bool,
  allLocalesCheckboxLabel: PropTypes.string.isRequired,
  onJobsNearMeClick: PropTypes.func.isRequired,
  jobsNearMeSelected: PropTypes.bool,
};

interface LocationChipProperties {
  id: string;
  name: string;
  onClick: MouseEventHandler<HTMLDivElement>;
  selected: boolean;
  jobsCount?: number;
}

interface CategoryOptionProperties {
  id: string;
  label: string;
  onClick: MouseEventHandler<HTMLDivElement>;
  selected: boolean;
  jobsCount?: number;
}

type SearchProperties = Omit<InferProps<
  typeof searchPropertyTypes
>, 'categoryOptions' | 'cities'> & {
  cities: LocationChipProperties[];
  categoryOptions: CategoryOptionProperties[];
  sortByDistanceInsertion: LocationChipProperties[];
  onJobsNearMeClick: () => void;
  jobsNearMeSelected?: boolean;
};

const SearchSkeleton = () => (
  <Skeleton variant="rectangular" width="100%">
    <MUITextField />
  </Skeleton>
);

const LocationBadgesSkeleton = styled(Skeleton)`
  grid-area: locationBadges;
  height: 48px;
  margin-left: -.75rem;
  min-width: 110%;

  @media (min-width: ${breakpoints.sm}px) {
    margin-left: 0;
    margin-right: 0;
    min-width: 100%;
  }
`;

const Search = ({
  keywords,
  searchFieldTitle,
  onSearchTrailingIconSelect,
  onSearchChange,
  locationSearch,
  locationSearchFieldTitle,
  onLocationSearchTrailingIconSelect,
  onLocationSearchChange,
  categoryOptions,
  cities,
  sortByDistanceInsertion,
  showJobsPageLoader,
  showCitiesLoader,
  handleAllLocalesFilterChange,
  showJobsForAllLocales,
  allLocalesCheckboxLabel,
  onJobsNearMeClick,
  jobsNearMeSelected,
}: SearchProperties) => {
  const locationChips = [...sortByDistanceInsertion, ...cities];

  return (
    <>
      <SearchInput>
        {showJobsPageLoader ? <SearchSkeleton /> : (
          <FullWidthSearchField
            id="jobs-search-text"
            name="search"
            label={searchFieldTitle}
            value={coalesceWithEmptyString(keywords)}
            onChange={onSearchChange}
            onClear={onSearchTrailingIconSelect}
          />
        )}
      </SearchInput>
      <LocationInput>
        {showJobsPageLoader ? <SearchSkeleton /> : (
          <LocationSearchContainer>
            <SearchField
              id="jobs-search-location"
              label={locationSearchFieldTitle}
              value={coalesceWithEmptyString(locationSearch)}
              onChange={onLocationSearchChange}
              onClear={onLocationSearchTrailingIconSelect}
            />
            <JobsNearMeIconButton
              onClick={onJobsNearMeClick}
              size="small"
              title="Jobs near me"
              selected={jobsNearMeSelected}
            >
              <LocationOnIcon />
            </JobsNearMeIconButton>
          </LocationSearchContainer>
        )}
      </LocationInput>
      { showCitiesLoader ? (
        <LocationBadgesSkeleton variant="rectangular">
          <StyledChip />
        </LocationBadgesSkeleton>
      ) : (
        <LocationBadges>
          <ContainerScrollable>
            {locationChips.map(({
              id,
              name,
              onClick,
              selected,
              jobsCount,
            }) => (
              <JobsStyledChip
                key={id}
                onClick={jobsCount === 0 ? undefined : onClick}
                hasCount={jobsCount !== undefined}
                label={
                  jobsCount === undefined ? name : (
                    <>
                      {name}
                      <ChipCount selected={selected}>{jobsCount}</ChipCount>
                    </>
                  )
                }
                color={selected ? 'primary' : 'default'}
                disabled={jobsCount === 0}
              />
            ))}
          </ContainerScrollable>
        </LocationBadges>
      ) }
      <CategoryBadges>
        <ContainerScrollable>
          {categoryOptions.map(({
            id,
            label,
            onClick,
            selected,
          }) => (
            <JobsStyledChip
              key={id}
              onClick={onClick}
              label={label}
              color={selected ? 'primary' : 'default'}
            />
          ))}
        </ContainerScrollable>
      </CategoryBadges>
      { handleAllLocalesFilterChange && (
        <AdditionalOptions>
          <Checkbox
            checked={showJobsForAllLocales ?? false}
            label={allLocalesCheckboxLabel}
            onChange={handleAllLocalesFilterChange}
          />
        </AdditionalOptions>
      )}
    </>
  );
};

Search.propTypes = searchPropertyTypes;

export default Search;
